import { useEffect, useRef, useState } from "react";
import { motion, animate } from "framer-motion";
import { EASE } from "@/components/Motion";

export const STATUS_LABELS = {
  pending: "Pending",
  in_progress: "In Progress",
  completed: "Completed",
  cancelled: "Cancelled",
  partial: "Partial",
  refunded: "Refunded",
};

const STATUS_STYLES = {
  pending: "border-amber-400/30 bg-amber-400/10 text-amber-300",
  in_progress: "border-sky-400/30 bg-sky-400/10 text-sky-300",
  completed: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300",
  cancelled: "border-rose-400/30 bg-rose-400/10 text-rose-300",
  partial: "border-orange-400/30 bg-orange-400/10 text-orange-300",
  refunded: "border-violet-400/30 bg-violet-400/10 text-violet-300",
};

export const StatusBadge = ({ status }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${STATUS_STYLES[status] || STATUS_STYLES.pending}`}
    data-testid={`status-badge-${status}`}
  >
    <span className="h-1 w-1 rounded-full bg-current" />
    {STATUS_LABELS[status] || status}
  </span>
);

export const StatCard = ({ icon: Icon, label, children, delay = 0, testid, accent = false }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.7, ease: EASE, delay }}
    whileHover={{ y: -4 }}
    className={`relative overflow-hidden rounded-2xl border p-5 transition-colors duration-300 sm:p-6 ${
      accent
        ? "border-gold/40 bg-gradient-to-br from-gold/[0.12] to-transparent"
        : "border-white/10 bg-panel/80 hover:border-gold/30"
    }`}
    data-testid={testid}
  >
    <div className="flex items-center justify-between">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">{label}</span>
      <Icon size={16} className={accent ? "text-gold" : "text-neutral-600"} />
    </div>
    <div className="mt-3 text-2xl font-bold tracking-tight text-neutral-50 sm:text-3xl">{children}</div>
  </motion.div>
);

export const Progress = ({ value }) => (
  <div className="h-1.5 w-full min-w-[64px] overflow-hidden rounded-full bg-white/5">
    <motion.div
      initial={{ width: 0 }}
      animate={{ width: `${value}%` }}
      transition={{ duration: 1.1, ease: EASE }}
      className="h-full rounded-full bg-gradient-to-r from-goldlight to-gold"
    />
  </div>
);

export const PageHeader = ({ title, sub, children }) => (
  <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
    <div>
      <h1 className="text-3xl font-bold tracking-tight text-neutral-50 sm:text-4xl">{title}</h1>
      {sub && <p className="mt-2 text-sm text-neutral-400">{sub}</p>}
    </div>
    {children}
  </div>
);

export const EmptyState = ({ icon: Icon, title, sub, testid }) => (
  <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-white/10 py-16 text-center" data-testid={testid}>
    <Icon size={28} className="text-gold/50" />
    <p className="mt-4 font-medium text-neutral-300">{title}</p>
    {sub && <p className="mt-1 text-sm text-neutral-500">{sub}</p>}
  </div>
);

export const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5 text-sm text-neutral-100 outline-none transition-colors duration-300 placeholder:text-neutral-600 focus:border-gold/60";

export const selectCls =
  "w-full appearance-none rounded-xl border border-white/10 bg-panel px-4 py-3.5 text-sm text-neutral-100 outline-none transition-colors duration-300 focus:border-gold/60";
